import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Shield, ShieldCheck, Lock, Users, GraduationCap, Sparkles, ArrowRight,
  ClipboardCheck, School, LayoutDashboard, BarChart3, Accessibility, Globe2,
  BookOpen, Presentation, Wand2, Trophy, ClipboardList, FileText, Palette,
  Check, Clock, Building2, Award, BookText,
} from "lucide-react";
import { SEO } from "@/components/SEO";

const Section = ({ eyebrow, title, subtitle, children }) => (
  <section className="py-16 sm:py-20 lg:py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl">
        {eyebrow && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 text-accent px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em]">
            {eyebrow}
          </span>
        )}
        <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.1]">{title}</h2>
        {subtitle && <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl">{subtitle}</p>}
      </div>
      <div className="mt-10">{children}</div>
    </div>
  </section>
);

const FeatureCard = ({ icon: Icon, title, desc, status, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.4 }}
    data-testid={testId}
    className="rounded-2xl border border-border bg-card p-5 hover:shadow-md hover:border-primary/30 transition-all"
  >
    <div className="flex items-start justify-between gap-2">
      <span className="grid place-items-center h-10 w-10 rounded-xl bg-accent/15 text-accent">
        <Icon className="h-5 w-5" />
      </span>
      {status === "shipped" && (
        <span className="text-[10px] font-bold uppercase tracking-wider rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200 px-2 py-0.5">
          Live
        </span>
      )}
      {status === "roadmap" && (
        <span className="text-[10px] font-bold uppercase tracking-wider rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 px-2 py-0.5">
          Roadmap
        </span>
      )}
    </div>
    <h3 className="mt-4 font-serif text-lg font-bold leading-tight">{title}</h3>
    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{desc}</p>
  </motion.div>
);

export const ForSchools = () => {
  return (
    <div className="grain min-h-screen">
      <SEO
        title="Lectura for Schools & Districts — FERPA & COPPA Compliant"
        description="Free Lectura for K-12 teachers. Rostering, Live Class Game Mode, LTI 1.3 on request. Standards-aligned. FERPA-ready and COPPA-compliant."
        canonical="https://getlectura.app/for-schools"
      />
      <header className="sticky top-0 z-40 backdrop-blur-md bg-background/80 border-b border-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary text-primary-foreground">
              <BookOpen className="h-5 w-5" />
            </span>
            <span className="font-serif text-2xl font-bold tracking-tight leading-none">
              Lectura<span className="text-accent">.</span>
            </span>
            <span className="ml-2 rounded-full bg-accent/15 text-accent px-2.5 py-1 text-xs font-semibold uppercase tracking-wider">For Schools</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/featured"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:border-primary/40 transition"
            >
              See featured guides
            </Link>
            <Link
              to="/"
              data-testid="schools-signup-cta"
              className="inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground px-4 py-2 text-sm font-semibold hover:opacity-90 transition"
            >
              <Sparkles className="h-4 w-4" /> Try free
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-16 sm:pt-24 pb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-2" data-testid="hero-trust-badges">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 px-3 py-1 text-xs font-bold uppercase tracking-wider">
                <ShieldCheck className="h-3.5 w-3.5" /> FERPA-Ready
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200 px-3 py-1 text-xs font-bold uppercase tracking-wider">
                <Lock className="h-3.5 w-3.5" /> No Student Data Training
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/40 dark:text-fuchsia-200 px-3 py-1 text-xs font-bold uppercase tracking-wider">
                <Award className="h-3.5 w-3.5" /> 13+ Age Gate
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200 px-3 py-1 text-xs font-bold uppercase tracking-wider" data-testid="badge-antivirus">
                <ShieldCheck className="h-3.5 w-3.5" /> Antivirus Scanned Uploads
              </span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="hero-standards-badges">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Standards:</span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200 px-3 py-1 text-xs font-bold uppercase tracking-wider" data-testid="standard-badge-common-core">
                <ShieldCheck className="h-3.5 w-3.5" /> Common Core
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-lime-100 text-lime-900 dark:bg-lime-900/40 dark:text-lime-200 px-3 py-1 text-xs font-bold uppercase tracking-wider" data-testid="standard-badge-ngss">
                <Sparkles className="h-3.5 w-3.5" /> NGSS
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-100 text-orange-900 dark:bg-orange-900/40 dark:text-orange-200 px-3 py-1 text-xs font-bold uppercase tracking-wider" data-testid="standard-badge-teks">
                <ClipboardCheck className="h-3.5 w-3.5" /> TEKS
              </span>
            </div>

            <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]" data-testid="schools-hero-heading">
              Turn every chapter into a
              <span className="text-accent"> standards-aligned</span> lecture,
              <br className="hidden lg:block" /> in the tools your teachers already use.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-3xl leading-relaxed">
              Lectura is the AI curriculum-and-assessment platform built for districts. Teachers upload a chapter or name a book;
              Lectura ships a full lecture, printable study guide, graded quiz — with every question tagged to Common Core, NGSS or TEKS — and posts it back into Google Classroom or Canvas in one click.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 text-sm font-semibold hover:opacity-90 transition"
              >
                <Sparkles className="h-4 w-4" /> Start free
              </Link>
              <a
                href="mailto:sales@lectura.app?subject=District%20pilot%20inquiry"
                data-testid="schools-contact-sales"
                className="inline-flex items-center gap-2 rounded-full border-2 border-border px-6 py-3 text-sm font-semibold hover:border-primary/40 transition"
              >
                <Building2 className="h-4 w-4" /> Talk to sales about a district pilot
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Compliance */}
      <Section
        eyebrow="Trust & Compliance"
        title="Built for districts that take student data seriously."
        subtitle="Every safeguard your IT department checks for during procurement — designed in, not bolted on."
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="trust-grid">
          <FeatureCard icon={ShieldCheck} title="FERPA-Ready" desc="Student records are treated as protected educational records. Data-processing agreement (DPA) available on request; right-to-erasure endpoint built in." status="shipped" />
          <FeatureCard icon={ShieldCheck} title="ClamAV Antivirus" desc="Every uploaded PDF, DOCX and image is streamed through a ClamAV daemon with 3.3M+ virus signatures before it's ever parsed. Infected files never reach our text extractor or your students." status="shipped" />
          <FeatureCard icon={Award} title="COPPA 13+ Age Gate" desc="Every new account must confirm they are 13 or older, or that their school has authorized use on behalf of a parent/guardian." status="shipped" />
          <FeatureCard icon={Lock} title="No Data Used to Train Models" desc="Prompts and student submissions are never used to train Gemini or any third-party model. Content stays yours, always." status="shipped" />
          <FeatureCard icon={Users} title="Right to Erasure" desc="Self-serve one-click account & data deletion. Every lecture, quiz score, chat and roster is purged in seconds." status="shipped" />
          <FeatureCard icon={Globe2} title="GDPR Ready" desc="Right-to-access, right-to-erasure and data-portability endpoints for EU/UK institutions." status="shipped" />
          <FeatureCard icon={Accessibility} title="WCAG 2.1 AA Accessibility" desc="Full keyboard navigation, ARIA labels, high-contrast palette, screen-reader tested. VPAT available." status="roadmap" />
          <FeatureCard icon={Shield} title="SOC 2 Type II" desc="Independent audit of security controls, availability and confidentiality. Report shared under NDA with district IT." status="roadmap" />
          <FeatureCard icon={ClipboardCheck} title="Content Moderation" desc="Ask-Professor prompts are logged and reviewable. Admins can block topics or approve student prompts." status="roadmap" />
          <FeatureCard icon={FileText} title="Sub-processor Transparency" desc="Public list of every third-party (Google Gemini for text/OCR, Stripe for billing) with the exact data they touch." status="shipped" />
        </div>
      </Section>

      {/* Curriculum Sync */}
      <Section
        eyebrow="Curriculum Sync — flagship for districts"
        title="One click turns your state standards into ready-to-teach, ready-to-grade lessons."
        subtitle="Import your existing course from Canvas or Google Classroom. Lectura generates a full standards-aligned library and pushes it back — every quiz question tagged, every gap flagged for the department chair."
      >
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-testid="curriculum-sync-grid">
          <FeatureCard icon={School} title="LMS Import" desc="Pull existing courses, chapter names and roster from Canvas, Google Classroom or Schoology in one click." status="roadmap" />
          <FeatureCard icon={ClipboardCheck} title="Standards Alignment" desc="Common Core, NGSS, TEKS, AP and state-level codes auto-tagged on every quiz question by Gemini 3.1 Pro." status="shipped" />
          <FeatureCard icon={BookText} title="Cornell Note-Taking Method" desc="Every study guide can render in the Cornell layout developed at Cornell University by Prof. Walter Pauk — Cues + Notes + Summary — the format most K-12 and university faculty already teach." status="shipped" />
          <FeatureCard icon={LayoutDashboard} title="Compliance Dashboard" desc="Department chairs see coverage per standard — 'HS-LS1 · 94% covered, 3 gaps' — and drill down to the exact missing objectives." status="roadmap" />
          <FeatureCard icon={ArrowRight} title="Grade Passback" desc="Assignment scores flow back into Canvas Gradebook or Classroom via LTI Advantage AGS." status="roadmap" />
        </div>
      </Section>

      {/* Pedagogical Differentiators */}
      <Section
        eyebrow="Pedagogy that teachers ask for"
        title="Not a chatbot. A full instructional-design assistant."
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="pedagogy-grid">
          <FeatureCard icon={Presentation} title="Rich Lectures + AI Slides" desc="45-50 slide decks with Nano Banana illustrations, presenter notes and a 3500-4500 word spoken-style lecture per chapter." status="shipped" />
          <FeatureCard icon={ClipboardList} title="Printable Study Guides" desc="Full chapter guide with key concepts, key terms, real section citations, and end-of-chapter Q&A with model answers." status="shipped" />
          <FeatureCard icon={Wand2} title="Graded Quizzes with Explanations" desc="Up to 100 questions per chapter, mastery-linked, with an explanation that teaches the concept — not just the answer." status="shipped" />
          <FeatureCard icon={Trophy} title="Live Class Game Mode" desc="Kahoot-style projector view + phone student view. Battle Royale elimination and Pop-Quiz Ambush keep the room in it." status="shipped" />
          <FeatureCard icon={GraduationCap} title="IEP / 504 Accommodations" desc="Per-student extended time, read-aloud narration, simplified vocabulary and chunked pacing. Real IEP compliance." status="roadmap" />
          <FeatureCard icon={BarChart3} title="Formative-Assessment Analytics" desc="Class-wide heat maps of weak concepts. Lectura auto-generates targeted re-teach lectures for the concepts a class missed." status="roadmap" />
        </div>
      </Section>

      {/* Admin Controls */}
      <Section
        eyebrow="Built for district CIOs"
        title="Admin controls that make a 5,000-teacher rollout feel like 5."
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-grid">
          <FeatureCard icon={LayoutDashboard} title="District Admin Console" desc="One dashboard for every teacher, class, assignment and dollar. Add/remove seats in bulk, see who's active, see who's stuck." status="roadmap" />
          <FeatureCard icon={Users} title="Bulk Seat Provisioning" desc="Upload a CSV or sync from your SIS. Lectura provisions accounts, sends invites and files them under the right school." status="shipped" />
          <FeatureCard icon={BarChart3} title="ROI & Usage Dashboards" desc="Hours saved per teacher, quiz-mastery lift per class, cost per student. The exact metrics a superintendent renews on." status="roadmap" />
          <FeatureCard icon={Palette} title="Custom Branding" desc="District logo, colors, custom domain (e.g. lectura.springfield.edu). Consistent identity across every teacher's dashboard." status="roadmap" />
          <FeatureCard icon={Globe2} title="Data Residency" desc="US-only or EU-only hosting options for districts with regional data requirements." status="roadmap" />
          <FeatureCard icon={GraduationCap} title="Age-Appropriate Mode" desc="Automatic vocabulary and tone adjustment by grade band — K-5, 6-8, 9-12, or college — set district-wide or per class." status="roadmap" />
        </div>
      </Section>

      {/* LMS & SSO */}
      <Section
        eyebrow="LMS & SSO Integration"
        title="Frictionless rollout — one login, every classroom."
        subtitle="Lectura is designed to disappear into the tools your teachers already open every morning."
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="lms-grid">
          <FeatureCard icon={School} title="Google Classroom" desc="Auto-roster sync, one-click assignment push into the Classroom stream, grade passback. The biggest K-12 stack in the US." status="roadmap" />
          <FeatureCard icon={Building2} title="Canvas (LTI 1.3)" desc="Full LTI 1.3 tool with NRPS roster sync + AGS grade passback. Works across every Canvas Cloud and self-hosted institution." status="roadmap" />
          <FeatureCard icon={School} title="Schoology / Blackboard / Moodle" desc="Same LTI 1.3 standard means one integration reaches all three at once." status="roadmap" />
          <FeatureCard icon={Users} title="Microsoft Teams for Education" desc="Auto-roster sync + assignment push. The second-largest K-12 stack after Google." status="roadmap" />
          <FeatureCard icon={ClipboardCheck} title="Clever & ClassLink" desc="Single-sign-on rostering from your Student Information System. One login pulls students, teachers, courses." status="roadmap" />
          <FeatureCard icon={Lock} title="SAML SSO" desc="Enterprise single-sign-on for staff via Google Workspace, Microsoft Entra, Okta." status="roadmap" />
        </div>
      </Section>

      {/* Bottom CTA */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-accent/30 bg-gradient-to-br from-accent/10 via-background to-primary/5 p-8 sm:p-12 text-center" data-testid="schools-bottom-cta">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold">
              Ready to bring Lectura to your school?
            </h2>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
              Start free with your own classes, or book a 30-minute district demo to walk through Curriculum Sync,
              standards alignment and rollout planning.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-7 py-3 text-sm font-semibold hover:opacity-90 transition"
              >
                <Sparkles className="h-4 w-4" /> Try free
              </Link>
              <a
                href="mailto:sales@lectura.app?subject=District%20demo%20request"
                className="inline-flex items-center gap-2 rounded-full border-2 border-border bg-background px-7 py-3 text-sm font-semibold hover:border-primary/40 transition"
              >
                <Building2 className="h-4 w-4" /> Book a district demo
              </a>
            </div>
            <p className="mt-6 text-xs text-muted-foreground">Data-processing agreement (DPA) and sub-processor list available on request.</p>
          </div>
        </div>
      </section>
    </div>
  );
};
